/**
 * Drill-down sheet for one Activity card: per-email rollups (visits,
 * dwell per tab, downloads, scope edits, FX switches, forwards) plus
 * the chronological human-readable event feed.
 */
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, MousePointerClick, Download, SlidersHorizontal, Repeat,
  Share2, ShieldCheck, ShieldAlert, Loader2, Layers, Mail, Clock,
  FileSpreadsheet, ExternalLink, FileDiff, CalendarDays, BookOpenCheck,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import RecipientsEditor from "./RecipientsEditor";
import { TEMP_META, timeAgo } from "../../pages/Activity";

const API = process.env.REACT_APP_BACKEND_URL;

const downloadArchive = async (archiveId, filename) => {
  try {
    const res = await axios.get(
      `${API}/api/activity/archives/${archiveId}/file`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "archived-download.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not fetch the archived copy", {
      description: e?.response?.status === 404
        ? "Copies are kept for 120 days - this one has expired."
        : e.message,
    });
  }
};

const EVENT_ICONS = {
  open: Eye,
  tab_change: MousePointerClick,
  download: Download,
  sample_change: SlidersHorizontal,
  fx_switch: Repeat,
  gate_passed: ShieldCheck,
  gate_blocked: ShieldAlert,
  gate_invite: Share2,
  timeline_country_select: CalendarDays,
  framework_click: BookOpenCheck,
};

const EVENT_TONES = {
  download: "text-emerald-600 bg-emerald-50 border-emerald-200",
  sample_change: "text-indigo-600 bg-indigo-50 border-indigo-200",
  fx_switch: "text-amber-600 bg-amber-50 border-amber-200",
  gate_blocked: "text-rose-600 bg-rose-50 border-rose-200",
  gate_invite: "text-sky-600 bg-sky-50 border-sky-200",
  timeline_country_select: "text-violet-600 bg-violet-50 border-violet-200",
  framework_click: "text-cyan-700 bg-cyan-50 border-cyan-200",
};

const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const fmtDwell = (ms) => {
  if (!ms) return "0s";
  const mins = ms / 60000;
  if (mins >= 1) return `${mins.toFixed(1)} min`;
  return `${Math.max(1, Math.round(ms / 1000))}s`;
};

function PersonCard({ person, index }) {
  const anon = person.email === "unidentified visitor";
  return (
    <div
      data-testid={`activity-trail-person-${index}`}
      className="bg-white border border-stone-200 rounded-xl p-4"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-semibold ${anon ? "bg-stone-100 text-stone-400" : "bg-[#1B4F8A]/10 text-[#1B4F8A]"}`}>
          {anon ? "?" : person.email[0].toUpperCase()}
        </span>
        <span className={`text-sm font-medium ${anon ? "text-stone-400 italic" : "text-slate-900"}`}>
          {anon ? "Unidentified visitor" : person.email}
        </span>
        <span className="ml-auto text-[11px] font-mono text-stone-400">
          {person.visits} visit{person.visits !== 1 ? "s" : ""} · {fmtDwell(person.total_dwell_ms)}
        </span>
      </div>

      <div className="flex items-center gap-3 mt-3 text-[11px] text-stone-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          first {fmtDateTime(person.first_open_at)}
        </span>
        <span>last {fmtDateTime(person.last_open_at)}{person.last_open_at ? ` (${timeAgo(person.last_open_at)})` : ""}</span>
      </div>

      {(person.tab_summary || []).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          {person.tab_summary.map((t) => (
            <span key={t.tab} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-stone-50 text-stone-600 border border-stone-200">
              {t.tab} · {t.pretty}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-stone-100 text-[11px] font-mono">
        <span className={person.downloads ? "text-emerald-700" : "text-stone-300"}>
          <Download className="w-3 h-3 inline mr-1" />{person.downloads} download{person.downloads !== 1 ? "s" : ""}
        </span>
        <span className={person.sample_changes ? "text-indigo-700" : "text-stone-300"}>
          <SlidersHorizontal className="w-3 h-3 inline mr-1" />{person.sample_changes} edit{person.sample_changes !== 1 ? "s" : ""}
        </span>
        <span className={person.fx_switches ? "text-amber-700" : "text-stone-300"}>
          <Repeat className="w-3 h-3 inline mr-1" />{person.fx_switches} FX
          {person.last_currency ? ` (${person.last_currency})` : ""}
        </span>
      </div>

      {(person.forwards || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {person.forwards.map((f, i) => (
            <p key={i} className="text-[11px] text-sky-700 flex items-center gap-1.5">
              <Share2 className="w-3 h-3" />
              Forwarded to {f.colleague_first_name ? `${f.colleague_first_name} ` : ""}
              &lt;{f.colleague_email}&gt; · {fmtDateTime(f.at)}
            </p>
          ))}
        </div>
      )}

      {(person.archived_downloads || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-100">
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mb-1.5">
            Their downloaded copies
          </p>
          <div className="space-y-1.5">
            {person.archived_downloads.map((d) => (
              <div key={d.archive_id} className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="text-[11px] text-stone-600 truncate flex-1" title={d.filename}>
                  {d.filename}
                </span>
                {d.state_summary && (
                  <span className="text-[10px] font-mono text-stone-400 shrink-0 hidden sm:inline">
                    {d.state_summary}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => downloadArchive(d.archive_id, d.filename)}
                  data-testid={`archive-download-${d.archive_id}`}
                  className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  View copy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FeedRow({ item }) {
  const Icon = EVENT_ICONS[item.event_type] || Eye;
  const tone = EVENT_TONES[item.event_type] || "text-stone-500 bg-stone-50 border-stone-200";
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md border shrink-0 mt-0.5 ${tone}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] text-slate-800 leading-snug">
          {item.line}
          {item.archive_id && (
            <button
              type="button"
              onClick={() => downloadArchive(item.archive_id, null)}
              data-testid={`feed-archive-${item.archive_id}`}
              className="ml-2 align-middle text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              View copy
            </button>
          )}
        </p>
        <p className="text-[11px] text-stone-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{fmtDateTime(item.at)}</span>
          {item.state_summary && (
            <span className="font-mono text-emerald-600">{item.state_summary}</span>
          )}
          {item.email && (
            <span className="inline-flex items-center gap-1 text-stone-500">
              <Mail className="w-3 h-3" />{item.email}
            </span>
          )}
          {item.option && (
            <span className="inline-flex items-center gap-1 text-indigo-500">
              <Layers className="w-3 h-3" />
              <span className="truncate max-w-[220px]">{item.option}</span>
            </span>
          )}
        </p>
      </div>
    </li>
  );
}

export default function ActivityTrail({ open, onClose, card, trail, loading }) {
  const meta = card ? (TEMP_META[card.temperature] || TEMP_META.never) : null;
  const TempIcon = meta?.icon;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        data-testid="activity-trail-sheet"
        className="w-full sm:max-w-2xl overflow-y-auto bg-stone-50 p-0"
      >
        {card && (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 bg-white border-b border-stone-200 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                {meta && (
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.pill}`}>
                    {TempIcon && <TempIcon className="w-3 h-3" />}
                    {meta.label}
                  </span>
                )}
                {card.temperature !== "never" && (
                  <span className="text-[11px] font-mono font-semibold text-stone-500">{card.score}/100</span>
                )}
                {card.bundle_size > 1 && (
                  <span
                    title={`Bundle options: ${(card.bundle_cids || []).join(", ")}`}
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                  >
                    <Layers className="w-3 h-3" />bundle of {card.bundle_size}
                  </span>
                )}
                <span className="ml-auto text-[10px] font-mono text-stone-400">#{card.cid}</span>
              </div>
              <SheetTitle className="text-lg text-[#1B4F8A] mt-1">
                {card.client_company || "Unknown client"}
              </SheetTitle>
              <SheetDescription className="text-xs text-stone-500 truncate">
                {card.project_reference || card.project_number || "-"}
              </SheetDescription>
              <div className="flex items-center gap-4 mt-2">
                <a
                  href={`/p/${card.token}?as=client`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="trail-open-as-client"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1B4F8A] hover:text-[#3EA8DC] transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  view as client
                </a>
                <a
                  href={`/p/${card.token}?as=staff`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="trail-open-as-staff"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-500 hover:text-stone-700 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  view as us
                </a>
              </div>
            </SheetHeader>

            <div className="px-6 py-5 flex flex-col gap-6">
              {/* Editable gate roster - staff add/remove recipient
                  emails right here (holiday cover, new procurement
                  contact) instead of re-running the share wizard. */}
              <RecipientsEditor token={card.token} />

              {loading && (
                <div className="flex items-center justify-center py-16 text-stone-400 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Loading audit trail…</span>
                </div>
              )}

              {!loading && trail && (
                <>
                  {(trail.revisions || []).length > 0 && (
                    <section data-testid="activity-trail-revisions">
                      <h3 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-2">
                        Proposal revisions
                      </h3>
                      <div className="space-y-2">
                        {trail.revisions.map((r) => (
                          <div key={r.rev} className="bg-violet-50/60 border border-violet-200 rounded-xl px-4 py-3">
                            <p className="text-[12px] font-semibold text-violet-800 flex items-center gap-2">
                              <FileDiff className="w-3.5 h-3.5" />
                              Revision {r.rev}
                              <span className="ml-auto font-mono font-normal text-[10px] text-violet-500">
                                {fmtDateTime(r.detected_at)}
                              </span>
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {(r.changes || []).map((c, i) => (
                                <li key={i} className="text-[11px] text-violet-700 font-mono">{c}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-stone-400 mt-1.5 italic">
                        Detected automatically when the proposal changes after the client first opened the link.
                      </p>
                    </section>
                  )}

                  <section>
                    <h3 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-3">
                      People ({(trail.people || []).length})
                    </h3>
                    {(trail.people || []).length === 0 ? (
                      <p className="text-sm text-stone-400" data-testid="activity-trail-no-people">
                        Nobody has opened this proposal yet.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {trail.people.map((p, i) => (
                          <PersonCard key={p.email} person={p} index={i} />
                        ))}
                      </div>
                    )}
                  </section>

                  <section>
                    <h3 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-1">
                      Activity feed
                    </h3>
                    {(trail.feed || []).length === 0 ? (
                      <p className="text-sm text-stone-400 mt-2">No client activity recorded yet.</p>
                    ) : (
                      <ul data-testid="activity-trail-feed" className="divide-y divide-stone-100 bg-white border border-stone-200 rounded-xl px-4">
                        {trail.feed.map((item, i) => (
                          <FeedRow key={i} item={item} />
                        ))}
                      </ul>
                    )}
                    {trail.feed_truncated && (
                      <p className="text-[11px] text-stone-400 mt-2">
                        Showing the most recent 500 events.
                      </p>
                    )}
                  </section>

                  {trail.staff_activity?.visits > 0 && (
                    <section data-testid="activity-trail-staff">
                      <h3 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-2">
                        Medicys team previews
                      </h3>
                      <div className="bg-stone-100/70 border border-stone-200 rounded-xl p-4">
                        <p className="text-[12px] text-stone-600 flex items-center gap-2">
                          <ShieldCheck className="w-3.5 h-3.5 text-stone-400" />
                          {trail.staff_activity.visits} internal view
                          {trail.staff_activity.visits !== 1 ? "s" : ""} by the
                          Medicys team
                          {trail.staff_activity.last_visit_at
                            ? ` · last ${timeAgo(trail.staff_activity.last_visit_at)}`
                            : ""}
                        </p>
                        <div className="mt-2 space-y-0.5">
                          {(trail.staff_activity.recent || []).map((v, i) => (
                            <p key={i} className="text-[11px] font-mono text-stone-400">
                              {fmtDateTime(v.at)}
                              {v.dwell_ms ? ` · ${fmtDwell(v.dwell_ms)}` : ""}
                            </p>
                          ))}
                        </div>
                        <p className="text-[10px] text-stone-400 mt-2 italic">
                          Staff previews never count towards the engagement score.
                        </p>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
